import type { Injector, Signal } from '@angular/core'
import { computed, inject, Injectable } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { ProjectStore } from '@awork/features/project/state/project.store'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import type { LinkedChat, Project } from '@awork/features/project/models/project.model'
import type { ProjectStatusType } from '@awork/features/project/models/project-status.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { getSortByGroup } from '@awork/features/project/functions/project-sorting'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { GroupByOptions } from '@awork/_shared/models/group-by-options.model'
import type { DateRange } from '@awork/_shared/functions/date-operations'
import { areDatesOverlapping } from '@awork/_shared/functions/date-operations'
import { Order } from '@awork/core/state/signal-store/types'
import type { QuickFilter } from '../types/project-page-settings'
import { Task } from '@awork/features/task/models/task.model'
import type { TaskTemplate } from '@awork/features/task/models/task-template.model'
import { WorkflowQuery } from '@awork/features/workflow/state/workflow.query'

export enum ProjectColumn {
  Name = 'name',
  ProjectKey = 'projectKey',
  Client = 'client',
  Type = 'type',
  Teams = 'teams',
  Tags = 'tags',
  StartDate = 'startDate',
  DueOn = 'dueOn',
  CreatedOn = 'createdOn',
  TrackedHours = 'trackedHours',
  Time = 'time',
  Budget = 'budget',
  Tasks = 'tasks',
  Progress = 'progress',
  Lead = 'lead',
  Creator = 'creator',
  Members = 'members',
  Status = 'status',
  StatusType = 'statusType',
  DueMonth = 'dueMonth',
  DueWeek = 'dueWeek',
  Workflow = 'workflow'
}

export const ProjectColumnWidth = new Map<ProjectColumn, number>([
  [ProjectColumn.Status, 110],
  [ProjectColumn.StatusType, 110],
  [ProjectColumn.Members, 110],
  [ProjectColumn.Lead, 110],
  [ProjectColumn.Creator, 140],
  [ProjectColumn.Progress, 100],
  [ProjectColumn.Tasks, 80],
  [ProjectColumn.Budget, 80],
  [ProjectColumn.Time, 80],
  [ProjectColumn.TrackedHours, 80],
  [ProjectColumn.ProjectKey, 80],
  [ProjectColumn.DueOn, 80],
  [ProjectColumn.DueWeek, 100],
  [ProjectColumn.DueMonth, 100],
  [ProjectColumn.StartDate, 80],
  [ProjectColumn.CreatedOn, 80],
  [ProjectColumn.Tags, 140],
  [ProjectColumn.Teams, 140],
  [ProjectColumn.Type, 110],
  [ProjectColumn.Client, 140],
  [ProjectColumn.ProjectKey, 80],
  [ProjectColumn.Workflow, 140]
])

export interface ClosedFilters {
  searchQuery: string
  statusType: ProjectStatusType | ProjectStatusType[]
}

interface SelectAllProjectOptions {
  limit?: number
  closedFilters?: Partial<ClosedFilters>
  projectStatusOrder?: boolean
  companyId?: string
  memberId?: string
  teamIds?: string[]
  projectGrouping?: GroupByOptions
  dateRange?: DateRange
  includeExternal?: boolean
  tags?: string[]
  ids?: string[]
  projectFilter?: (project: Project) => boolean
}

@Injectable({ providedIn: 'root' })
export class ProjectQuery extends EntitySignalQuery<Project> {
  protected store: ProjectStore

  private userQuery = inject(UserQuery)
  private settingsQuery = inject(SettingsQuery)
  private workflowQuery = inject(WorkflowQuery)

  constructor() {
    const store = inject(ProjectStore)

    super(store)

    this.store = store
  }

  /**
   * Selects the assigned active projects
   * @param {Injector} injector
   * @param {number} limit
   * @param {string} searchQuery
   * @param {boolean} [active=true]
   * @param {boolean} [includeExternal=false]
   * @param {(project: Project) => boolean} projectFilter Additional project filter applied before the limit.
   * @returns {Observable<Project[]>}
   * @deprecated
   */
  selectAssignedActiveProjects(
    injector: Injector,
    limit?: number,
    searchQuery?: string,
    active = true,
    includeExternal = false,
    projectFilter?: (project: Project) => boolean
  ): Observable<Project[]> {
    return this.selectAll(injector, {
      filterBy: project =>
        this.assignedActiveProjectsFilter(project, searchQuery, active) &&
        this.filterExternalProjects(project, includeExternal) &&
        (projectFilter?.(project) ?? true),
      sortBy: (projectA, projectB) => projectA.name.localeCompare(projectB.name),
      limitTo: limit
    }).pipe(map(projects => this.mapEntities(projects)))
  }

  /**
   * Gets the assigned active projects
   * @param {boolean} [active=true]
   * @param {{ limit?: number; searchQuery?: string; includeExternal: boolean; }} options
   * @param options.limit
   * @param options.searchQuery
   * @param options.includeExternal
   * @returns {Signal<Project[]>}
   */
  queryAssignedActiveProjects(
    active = true,
    options?: {
      limit?: number
      searchQuery?: string
      includeExternal: boolean
    }
  ): Signal<Project[]> {
    const { limit, searchQuery, includeExternal } = options || {}

    return computed(() => {
      const currentUserId = this.userQuery.getActive()?.id || this.userQuery.getCurrentUser()?.id
      const projects = this.queryAll({
        filterBy: project =>
          this.assignedActiveProjectsFilter(project, searchQuery, active, currentUserId) &&
          this.filterExternalProjects(project, includeExternal),
        sortBy: (projectA, projectB) => this.sortProjects(projectA, projectB),
        limitTo: limit
      })

      return projects()
    })
  }

  /**
   * Gets the external projects
   * @param {boolean} [isActive=true]
   * @returns {Signal<Project[]>}
   */
  queryExternalProjects(isActive = true): Signal<Project[]> {
    return this.queryAll({
      filterBy: project =>
        !!project.isExternal && (!isActive || this.progressFilter(project) || this.notStartedFilter(project)),
      sortBy: (projectA, projectB) => this.sortProjects(projectA, projectB)
    })
  }

  /**
   * Gets the count of assigned projects
   * @param {string} searchQuery
   * @param {boolean} [active=true]
   * @param {boolean} [includeExternal=false]
   * @param {(project: Project) => boolean} projectFilter Additional project filter.
   * @returns {number}
   */
  assignedActiveProjectsCount(
    searchQuery?: string,
    active = true,
    includeExternal = false,
    projectFilter?: (project: Project) => boolean
  ): number {
    return this.getCount(
      project =>
        this.assignedActiveProjectsFilter(project, searchQuery, active) &&
        this.filterExternalProjects(project, includeExternal) &&
        (projectFilter?.(project) ?? true)
    )
  }

  /**
   * Filters out assigned and active projects
   * @param {Project} project
   * @param {string} searchQuery
   * @param {boolean} [active=true]
   * @param {string} currentUserId
   * @returns {boolean}
   */
  assignedActiveProjectsFilter(project: Project, searchQuery?: string, active = true, currentUserId?: string): boolean {
    return (
      this.assignedFilter(project, currentUserId) &&
      this.searchFilter(project, searchQuery) &&
      (!active || this.progressFilter(project))
    )
  }

  /**
   * Gets the not-done (not-started, progress) projects
   * @param {boolean} [includeExternal=false]
   * @returns {Signal<Project[]>}
   */
  queryNotDoneProjects(includeExternal = false): Signal<Project[]> {
    return this.queryAll({
      filterBy: project => {
        const type = project.projectStatus?.type
        return (type === 'not-started' || type === 'progress') && this.filterExternalProjects(project, includeExternal)
      },
      sortBy: (projectA, projectB) => this.sortProjects(projectA, projectB)
    })
  }

  /**
   * Gets the visible columns in list
   * When is not widescreen the array is used to determine which actions are visible
   * @returns {Signal<ProjectColumn[]>}
   */
  queryColumns(): Signal<ProjectColumn[]> {
    const defaultLargeList = [
      ProjectColumn.Tags,
      ProjectColumn.DueOn,
      ProjectColumn.Progress,
      ProjectColumn.Tasks,
      ProjectColumn.Lead,
      ProjectColumn.Status,
      ProjectColumn.Type
    ]

    return computed(() => {
      const listColumns = this.settingsQuery.queryListColumns('project')

      return (listColumns()?.largeList as ProjectColumn[]) || defaultLargeList
    })
  }

  /**
   * Gets the visible columns for the projects timeline sidebar
   * @returns {Signal<ProjectColumn[]>}
   */
  queryTimelineColumns(): Signal<ProjectColumn[]> {
    return computed(() => {
      const listColumns = this.settingsQuery.queryListColumns('projectTimeline')

      return (listColumns()?.largeList as ProjectColumn[]) || []
    })
  }

  /**
   * Gets the projects by status type
   * @param {ProjectStatusType | ProjectStatusType[]} statusType
   * @param {{ limit?: number; searchQuery?: string; companyId?: string; memberId?: string; projectGrouping?: GroupByOptions; currentUserAssigned?: boolean; dateRange?: DateRange; includeExternal: boolean; }} options
   * @param options.limit
   * @param options.searchQuery
   * @param options.companyId
   * @param options.memberId
   * @param options.projectGrouping
   * @param options.currentUserAssigned
   * @param options.dateRange
   * @param options.includeExternal
   * @returns {Signal<Project[]>}
   */
  queryByStatusType(
    statusType?: ProjectStatusType | ProjectStatusType[],
    options?: {
      limit?: number
      searchQuery?: string
      companyId?: string
      memberId?: string
      projectGrouping?: GroupByOptions
      currentUserAssigned?: boolean
      dateRange?: DateRange
      includeExternal: boolean
    }
  ): Signal<Project[]> {
    const {
      limit,
      searchQuery,
      companyId,
      memberId,
      projectGrouping,
      currentUserAssigned,
      dateRange,
      includeExternal
    } = options || {}

    const projects = statusType
      ? this.queryAll({
          filterBy: project => {
            return (
              this.statusTypeFilter(project, statusType) &&
              this.searchFilter(project, searchQuery) &&
              this.companyFilter(project, companyId) &&
              this.memberFilter(project, memberId) &&
              this.currentUserFilter(project, currentUserAssigned) &&
              this.dateRangeFilter(project, dateRange) &&
              this.filterExternalProjects(project, includeExternal)
            )
          },
          sortBy: (projectA, projectB) => this.getSort(projectA, projectB, undefined, projectGrouping),
          limitTo: limit
        })
      : this.queryAllProjects({
          limit,
          closedFilters: { searchQuery },
          projectStatusOrder: true,
          companyId,
          memberId,
          projectGrouping,
          dateRange
        })

    return projects
  }

  /**
   * Selects project by id
   * @param {string} id
   * @param {Injector} injector
   * @returns {Observable<Project>}
   * @deprecated
   */
  selectProject(id: string, injector: Injector): Observable<Project> {
    return this.selectEntity(id, injector).pipe(
      map(project => {
        if (project) {
          project.linkedChats = this.filterUniqueLinkedChats(project)

          return this.mapEntity(project)
        }

        return null
      })
    )
  }

  /**
   * Filters linked chats to avoid duplicated providers
   * @param {Project} project
   * @returns {LinkedChat[]}
   */
  private filterUniqueLinkedChats(project: Project): LinkedChat[] {
    return (
      project.linkedChats &&
      project.linkedChats.reduce((uniqueArray, linkedChat) => {
        const providerExist = !!uniqueArray.find(uniqueLinkedChat => uniqueLinkedChat.provider === linkedChat.provider)

        // If the provider is not in the array, add it
        if (!providerExist) {
          uniqueArray.push(linkedChat)
        }

        return uniqueArray
      }, [])
    )
  }

  /**
   * Gets the project's linkedChats by provider
   * @param {string} projectId
   * @param provider
   * @returns {LinkedChat}
   */
  getLinkedChatsByProvider(projectId: string, provider): LinkedChat {
    const project = this.getProject(projectId)
    return project && project.linkedChats && project.linkedChats.find(linkedChat => linkedChat.provider === provider)
  }

  /**
   * Gets project by id
   * @param {string} id
   * @returns {Project}
   */
  getProject(id: string): Project {
    const project = this.getEntity(id)

    return this.mapEntity(project)
  }

  /**
   * Retrieves the project entity associated with the provided task or template.
   * @param {Task | TaskTemplate | null} task
   * @returns {Project | null}
   */
  getProjectForTask(task?: Task | TaskTemplate | null): Project | null {
    if (!(task instanceof Task)) {
      return null
    }

    const projectId = task.projectId || task.entityId

    if (!projectId) {
      return null
    }

    return this.getProject(projectId)
  }

  /**
   * Gets a project by its projectKey
   * @param {string} projectKey
   * @param {boolean} [getExternal=false]
   * @returns {Project | null}
   */
  getProjectByKey(projectKey: string, getExternal = false): Project | null {
    const project = this.getAll().find(
      p => p.projectKey?.toUpperCase() === projectKey.toUpperCase() && p.isExternal === getExternal
    )

    return project ? this.mapEntity(project) : null
  }

  /**
   * Queries all projects by a projectKey
   * @param {string} projectKey
   * @returns {Project[]}
   */
  getExternalProjectsByKey(projectKey: string): Project[] {
    const projects = this.getAll().filter(
      p => p.projectKey?.toUpperCase() === projectKey.toUpperCase() && p.isExternal === true
    )
    return this.mapEntities(projects)
  }

  /**
   * Gets the project by id
   * @param {string} id
   * @param {{ equalityCheck?: 'default' | 'resourceVersion'; }} options The options to use
   * @returns {Signal<Project>}
   * @param {'default' | 'resourceVersion'} options.equalityCheck - The equality check to use
   */
  queryProject(id: string, options?: { equalityCheck?: 'default' | 'resourceVersion' }): Signal<Project> {
    return computed(
      () => {
        const project = this.queryEntity(id)
        return project()
      },
      {
        equal: (a, b) => this.equalityCheck(a, b, options)
      }
    )
  }

  /**
   * Checks if 2 projects are equal by equality check option
   * @param {Project} a
   * @param {Project} b
   * @param {{ equalityCheck?: 'default' | 'resourceVersion'; }} options The options to use
   * @returns {boolean}
   * @param {'default' | 'resourceVersion'} options.equalityCheck - The equality check to use
   * @private
   */
  private equalityCheck(a: Project, b: Project, options?: { equalityCheck?: 'default' | 'resourceVersion' }): boolean {
    if (!options?.equalityCheck || options.equalityCheck === 'default') {
      return a === b
    }

    return a?.resourceVersion === b?.resourceVersion
  }

  /**
   * Gets the active project
   * @returns {Signal<Project>}
   */
  queryActiveProject(): Signal<Project> {
    return computed(() => {
      const project = this.queryActive()
      return project()
    })
  }

  /**
   * Gets the active project's id
   * @returns {Signal<string>}
   */
  queryActiveProjectId(): Signal<string> {
    return computed(() => {
      const project = this.queryActiveProject()
      return project()?.id
    })
  }

  /**
   * Gets projects by ids
   * @param {string[]} ids
   * @returns {Signal<Project[]>}
   */
  queryProjects(ids: string[]): Signal<Project[]> {
    return computed(() => {
      const projects = this.queryMany(ids)
      return [...projects()].sort(this.sortProjects)
    })
  }

  /**
   * Gets projects by ids
   * @param {string[]} ids
   * @returns {Project[]}
   */
  getProjects(ids: string[]): Project[] {
    const projects = this.getAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: project => ids.includes(project.id)
    })

    return this.mapEntities(projects)
  }

  /**
   * Selects all projects
   * @param {Injector} injector
   * @param {SelectAllProjectOptions} [options={ projectStatusOrder: true, includeExternal: false }]
   * @returns {Observable<Project[]>}
   * @deprecated
   */
  selectAllProjects(
    injector: Injector,
    options: SelectAllProjectOptions = { projectStatusOrder: true, includeExternal: false }
  ): Observable<Project[]> {
    const {
      ids,
      tags,
      limit,
      closedFilters,
      projectStatusOrder,
      companyId,
      memberId,
      teamIds,
      projectGrouping,
      dateRange,
      includeExternal,
      projectFilter
    } = options

    return this.selectAll(injector, {
      sortBy: (projectA, projectB) => this.getSort(projectA, projectB, projectStatusOrder, projectGrouping),
      filterBy: project =>
        this.idFilter(project, ids) &&
        this.teamFilter(project, teamIds) &&
        this.searchFilter(project, closedFilters?.searchQuery) &&
        this.tagsFilter(project, tags) &&
        this.statusTypeFilter(project, closedFilters?.statusType) &&
        this.companyFilter(project, companyId) &&
        this.memberFilter(project, memberId) &&
        this.filterExternalProjects(project, includeExternal) &&
        this.dateRangeFilter(project, dateRange) &&
        (projectFilter?.(project) ?? true),
      limitTo: limit
    }).pipe(map(projects => this.mapEntities(projects)))
  }

  /**
   * Gets all projects
   * @param {SelectAllProjectOptions} [options={ projectStatusOrder: true, includeExternal: false }]
   * @returns {Signal<Project[]>}
   */
  queryAllProjects(
    options: SelectAllProjectOptions = { projectStatusOrder: true, includeExternal: false }
  ): Signal<Project[]> {
    const {
      limit,
      ids,
      tags,
      closedFilters,
      projectStatusOrder,
      companyId,
      memberId,
      teamIds,
      projectGrouping,
      dateRange,
      includeExternal
    } = options

    return this.queryAll({
      sortBy: (projectA, projectB) => this.getSort(projectA, projectB, projectStatusOrder, projectGrouping),
      filterBy: project =>
        this.idFilter(project, ids) &&
        this.teamFilter(project, teamIds) &&
        this.searchFilter(project, closedFilters?.searchQuery) &&
        this.statusTypeFilter(project, closedFilters?.statusType) &&
        this.companyFilter(project, companyId) &&
        this.memberFilter(project, memberId) &&
        this.tagsFilter(project, tags) &&
        this.filterExternalProjects(project, includeExternal) &&
        this.dateRangeFilter(project, dateRange),
      limitTo: limit
    })
  }

  /**
   * Gets all projects for the Planner
   * @param {SelectAllProjectOptions} [options={ projectStatusOrder: true, includeExternal: false }]
   * @returns {Signal<Project[]>}
   */
  queryPlannerProjects(
    options: SelectAllProjectOptions = { projectStatusOrder: true, includeExternal: false }
  ): Signal<Project[]> {
    const {
      limit,
      ids,
      tags,
      closedFilters,
      projectStatusOrder,
      companyId,
      memberId,
      teamIds,
      projectGrouping,
      dateRange,
      includeExternal
    } = options

    return this.queryAll({
      sortBy: (projectA, projectB) => this.getSort(projectA, projectB, projectStatusOrder, projectGrouping),
      filterBy: project =>
        this.idOrTeamIdsFilter(project, { projectIds: ids, teamIds }) &&
        this.searchFilter(project, closedFilters?.searchQuery) &&
        this.statusTypeFilter(project, closedFilters?.statusType) &&
        this.companyFilter(project, companyId) &&
        this.memberFilter(project, memberId) &&
        this.tagsFilter(project, tags) &&
        this.filterExternalProjects(project, includeExternal) &&
        this.dateRangeFilter(project, dateRange),
      limitTo: limit
    })
  }

  /**
   * Selects all projects according to the searchQuery
   * This is used for global search
   * @param {Injector} injector
   * @param {string} searchQuery
   * @param {number} limit
   * @param {boolean} [includeExternal=false]
   * @returns {Observable<Project[]>}
   * @deprecated
   */
  searchProjects(
    injector: Injector,
    searchQuery: string,
    limit?: number,
    includeExternal = false
  ): Observable<Project[]> {
    return this.selectAll(injector, {
      limitTo: limit,
      filterBy: project =>
        (searchQuery === '*' ||
          (project.name && project.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (project.company && project.company.name.toLowerCase().includes(searchQuery.toLowerCase()))) &&
        this.filterExternalProjects(project, includeExternal),
      sortBy: (projectA, projectB) => {
        // Set scores
        projectA.score = projectA.score ? projectA.score : 0
        projectB.score = projectB.score ? projectB.score : 0

        // if the scores are different, use them to order (desc). Otherwise, order by name (asc)
        if (projectA.score !== projectB.score) {
          return projectB.score - projectA.score
        } else if (projectA.name && projectB.name) {
          return projectA.name.localeCompare(projectB.name)
        } else {
          return 0
        }
      }
    }).pipe(map(projects => this.mapEntities(projects)))
  }

  /**
   * Gets projects applying entity and quick filters
   * @param {{ entityFilterType?: 'company' | 'user'; entityFilterId?: string; quickFilter?: QuickFilter; quickStatusFilterSelected?: ProjectStatusType[]; searchQuery?: string; dateRange?: DateRange; includeExternal?: boolean; }} filters
   * @param filters.entityFilterType
   * @param {GroupByOptions} groupOption
   * @param filters.entityFilterId
   * @param filters.quickFilter
   * @param filters.quickStatusFilterSelected
   * @param filters.searchQuery
   * @param filters.dateRange
   * @param filters.includeExternal
   * @returns {Signal<Project[]>}
   */
  queryProjectByEntityAndQuickFilter(
    filters: {
      entityFilterType?: 'company' | 'user'
      entityFilterId?: string
      quickFilter?: QuickFilter
      quickStatusFilterSelected?: ProjectStatusType[]
      searchQuery?: string
      dateRange?: DateRange
      includeExternal?: boolean
    },
    groupOption?: GroupByOptions
  ): Signal<Project[]> {
    const projects =
      !filters.entityFilterType && filters.quickFilter === 'all'
        ? this.queryAllProjects({
            closedFilters: { searchQuery: filters.searchQuery },
            projectGrouping: groupOption,
            dateRange: filters.dateRange,
            includeExternal: filters.includeExternal
          })
        : this.queryByStatusType(filters.quickStatusFilterSelected, {
            searchQuery: filters.searchQuery,
            companyId: filters.entityFilterType === 'company' ? filters.entityFilterId : null,
            memberId: filters.entityFilterType === 'user' ? filters.entityFilterId : null,
            projectGrouping: groupOption,
            currentUserAssigned: filters.quickFilter === 'my',
            dateRange: filters.dateRange,
            includeExternal: filters.includeExternal
          })

    return projects
  }

  /**
   * Gets the count of projects
   * @param {Partial<ClosedFilters>} closedFilters
   * @param {string[]} filterIds
   * @param {boolean} [includeExternal=false]
   * @param {(project: Project) => boolean} projectFilter Additional project filter.
   * @returns {number}
   */
  getProjectCount(
    closedFilters?: Partial<ClosedFilters>,
    filterIds?: string[],
    includeExternal = false,
    projectFilter?: (project: Project) => boolean
  ): number {
    return this.getCount(
      project =>
        this.searchFilter(project, closedFilters?.searchQuery, filterIds) &&
        this.statusTypeFilter(project, closedFilters?.statusType) &&
        this.filterExternalProjects(project, includeExternal) &&
        (projectFilter?.(project) ?? true)
    )
  }

  /**
   * Gets the count of projects based on search and external filters
   * @param {string} searchQuery Optional search query to filter projects
   * @param {boolean} [includeExternal=false] Whether to include external projects
   * @returns {Signal<number>}
   */
  queryProjectCount(searchQuery?: string, includeExternal = false): Signal<number> {
    return this.queryCount(
      project => this.searchFilter(project, searchQuery) && this.filterExternalProjects(project, includeExternal)
    )
  }

  /**
   * Gets the count of internal projects
   * @returns {Signal<number>}
   */
  get queryInternalCount(): Signal<number> {
    return this.queryCount(project => !project.isExternal)
  }

  /**
   * Gets the tasksLoaded flag of a project
   * @param {string} projectId
   * @returns {boolean}
   */
  areTasksLoaded(projectId: string): boolean {
    const project = this.getEntity(projectId)
    return project ? project.tasksLoaded : false
  }

  /**
   * Filter used to get assigned projects.
   * @param {Project} project
   * @param {string} currentUserId
   * @returns {boolean}
   */
  private assignedFilter(project: Project, currentUserId?: string): boolean {
    const resolvedCurrentUserId = currentUserId || this.userQuery.getActive()?.id || this.userQuery.getCurrentUser()?.id
    return (
      project.members &&
      resolvedCurrentUserId &&
      project.members.some(member => member.userId === resolvedCurrentUserId)
    )
  }

  /**
   * Filter used to get projects in progress and subscription
   * @param {Project} project
   * @returns {boolean}
   */
  private progressFilter(project: Project): boolean {
    return project.projectStatus?.type === 'progress'
  }

  /**
   * Filter used to get not-started projects
   * @param {Project} project
   * @returns {boolean}
   */
  private notStartedFilter(project: Project): boolean {
    return project.projectStatus?.type === 'not-started'
  }

  /**
   * Returns whether the project should be included according to the includeExternal flag
   * @param {Project} project
   * @param {boolean} includeExternal
   * @returns {boolean}
   */
  private filterExternalProjects(project: Project, includeExternal: boolean): boolean {
    return includeExternal || !project.isExternal
  }

  /**
   * Filter used for search by name, tag, company name, and project key
   * @param {Project} project
   * @param {string} searchQuery
   * @param {string[]} filterIds
   * @returns {boolean}
   */
  searchFilter(project: Project, searchQuery: string, filterIds?: string[]): boolean {
    if (!searchQuery) {
      return true
    }

    const isFiltered = filterIds && filterIds.includes(project.id)

    // If the project is filtered or there is no search query, the rest is not important
    if (isFiltered) {
      return false
    }

    const normalizedSearch = searchQuery.toLowerCase().replace(/\s+/g, ' ')

    const includesName = project.name?.toLowerCase().replace(/\s+/g, ' ').includes(normalizedSearch)
    const includesTag = project.tags?.some(t => t.name.toLowerCase().replace(/\s+/g, ' ').includes(normalizedSearch))
    const includesCompanyName = project?.company?.name?.toLowerCase().replace(/\s+/g, ' ').includes(normalizedSearch)
    const includesProjectKey = project.projectKey?.toLowerCase().includes(normalizedSearch)

    let includesWorkflow = false

    if (project.workflowId) {
      const workflowName = this.workflowQuery.getEntity(project.workflowId)?.name?.toLowerCase().replace(/\s+/g, ' ')

      includesWorkflow = workflowName ? workflowName.includes(normalizedSearch) : false
    }

    return includesName || includesTag || includesCompanyName || includesProjectKey || includesWorkflow
  }

  /**
   * Filters projects by status type or status types
   * @param {Project} project
   * @param {ProjectStatusType | ProjectStatusType[]} statusType
   * @returns {boolean}
   * @private
   */
  private statusTypeFilter(project: Project, statusType: ProjectStatusType | ProjectStatusType[]): boolean {
    if (!statusType || !statusType.length) {
      return true
    }

    const statusTypes = Array.isArray(statusType) ? statusType : [statusType]

    return statusTypes.some(type => project.projectStatus?.type === type)
  }

  /**
   * Filters projects by id
   * @param {Project} project
   * @param {string[]} projectIds
   * @returns {boolean}
   * @private
   */
  private idFilter(project: Project, projectIds: string[]): boolean {
    if (!projectIds || !projectIds.length) {
      return true
    }

    return projectIds.includes(project.id)
  }

  /**
   * Filters projects by projectId OR teamIds
   * @param {Project} project
   * @param {{ projectIds: string[]; teamIds: string[]; }} options
   * @param options.projectIds
   * @param options.teamIds
   * @returns {boolean}
   * @private
   */
  private idOrTeamIdsFilter(project: Project, options: { projectIds: string[]; teamIds: string[] }): boolean {
    const { projectIds = [], teamIds = [] } = options

    if (!projectIds.length && !teamIds.length) {
      return true
    }

    if (!projectIds.length) {
      return this.teamFilter(project, teamIds)
    }

    if (!teamIds.length) {
      return this.idFilter(project, projectIds)
    }

    return this.idFilter(project, projectIds) || this.teamFilter(project, teamIds)
  }

  /**
   * Filters project by company
   * @param {Project} project
   * @param {string} companyId
   * @returns {boolean}
   */
  private companyFilter(project: Project, companyId: string): boolean {
    if (!companyId) {
      return true
    }

    return project.companyId === companyId
  }

  /**
   * Filters project by member
   * @param {Project} project
   * @param {string} memberId
   * @returns {boolean}
   */
  private memberFilter(project: Project, memberId: string): boolean {
    if (!memberId) {
      return true
    }

    return project.members && project.members.some(member => member.id === memberId)
  }

  /**
   * Filters project by team
   * @param {Project} project
   * @param {string[]} teamIds
   * @returns {boolean}
   */
  private teamFilter(project: Project, teamIds: string[]): boolean {
    if (!teamIds?.length) {
      return true
    }

    return project.teams?.some(team => teamIds.some(idToFilter => idToFilter === team.id))
  }

  /**
   * Filters project by tags
   * @param {Project} project
   * @param {string[]} tags
   * @returns {boolean}
   */
  private tagsFilter(project: Project, tags: string[]): boolean {
    if (!tags?.length) {
      return true
    }

    return project.tags?.some(tag => tags.includes(tag.name))
  }

  /**
   * Filters project by dateRange
   * @param {Project} project
   * @param {DateRange} dateRange
   * @returns {boolean}
   */
  private dateRangeFilter(project: Project, dateRange: DateRange): boolean {
    if (!dateRange) {
      return true
    }

    if (!project.startDate || !project.dueDate) {
      return false
    }

    return areDatesOverlapping(
      { start: new Date(project.startDate), end: new Date(project.dueDate) },
      { start: dateRange.startDate, end: dateRange.endDate }
    )
  }

  /**
   * Filters project by the current user being a member
   * @param {Project} project
   * @param {boolean} currentUserAssigned
   * @returns {boolean}
   * @private
   */
  private currentUserFilter(project: Project, currentUserAssigned: boolean): boolean {
    if (!currentUserAssigned) {
      return true
    }

    const currentUser = this.userQuery.getCurrentUser()

    return project.members?.some(member => member.userId === currentUser.id)
  }

  /**
   * Gets the sort used for the project queries
   * Sorting: by projectStatus.typeOrder and project name, ascending
   * @param {Project} projectA
   * @param {Project} projectB
   * @param {boolean} [projectStatusOrder=true] To apply sorting by project status type order
   * @param {GroupByOptions} projectGrouping
   * @returns {number}
   */
  private getSort(
    projectA: Project,
    projectB: Project,
    projectStatusOrder = true,
    projectGrouping?: GroupByOptions
  ): number {
    if (projectGrouping && projectGrouping !== GroupByOptions.none) {
      return getSortByGroup(projectA, projectB, this.workflowQuery, projectGrouping)
    }

    if (projectA.projectStatus && projectB.projectStatus && projectStatusOrder) {
      if (projectA.projectStatus.typeOrder !== projectB.projectStatus.typeOrder) {
        return projectA.projectStatus.typeOrder - projectB.projectStatus.typeOrder
      } else {
        return new Date(projectB.createdOn).getTime() - new Date(projectA.createdOn).getTime()
      }
    }

    if (projectA.name && projectB.name) {
      return projectA.name.localeCompare(projectB.name)
    }

    return 0
  }

  /**
   * Get all projects the user is a member of
   * @param {string} assigneeId
   * @param {boolean} [includeExternal=false]
   * @returns {Project[]}
   */
  getProjectsByAssigneeId(assigneeId: string, includeExternal = false): Project[] {
    const projects = this.getAll()

    if (!projects.length) {
      return []
    }

    const filteredProjects = projects.filter(
      p => p.members.some(member => member.userId === assigneeId) && this.filterExternalProjects(p, includeExternal)
    )

    return this.mapEntities(filteredProjects)
  }

  /**
   * Query projects by filter expression
   * @param {string} filterExpression
   * @returns {Signal<Project[]>}
   */
  queryProjectsByFilterExpression(filterExpression: string): Signal<Project[]> {
    return computed(() => {
      const projects = this.queryAllByFilterExpression(filterExpression)

      return [...projects()].sort(this.sortProjects)
    })
  }

  /**
   * Sort function to sort projects by name
   * @param {Project} projectA
   * @param {Project} projectB
   * @returns {number}
   */
  private sortProjects(projectA: Project, projectB: Project): number {
    return projectA.name.localeCompare(projectB.name)
  }
}
